import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import ShelfRow from "@/components/ShelfRow";
import { useUserData } from "@/hooks/useUserData";
import { usePreferences } from "@/hooks/usePreferences";
import { useAuth } from "@/contexts/AuthContext";
import { useAgeGate } from "@/hooks/useAgeGate";
import AgeGateModal from "@/components/AgeGateModal";
import AgeBlockedScreen from "@/components/AgeBlockedScreen";
import type { MovieData } from "@/components/SwipeCard";
import ayerisLogo from "@/assets/ayeris-logo.png";
import TmdbAttribution from "@/components/TmdbAttribution";

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { getOnboarded } = usePreferences();
  const { getWatchHistory, getBookmarks, getReviewPending, getHiddenTitles } = useUserData();
  const { needsReverification, setNeedsReverification } = useAgeGate();
  const [ageBlocked, setAgeBlocked] = useState(false);
  const [watchHistory, setWatchHistory] = useState<MovieData[]>([]);
  const [bookmarked, setBookmarked] = useState<MovieData[]>([]);
  const [reviewPending, setReviewPending] = useState<MovieData[]>([]);
  const [hiddenTitles, setHiddenTitles] = useState<MovieData[]>([]);

  useEffect(() => {
    if (loading || !user) return;
    const check = async () => {
      const onboarded = await getOnboarded();
      if (!onboarded) navigate("/onboarding", { replace: true });
    };
    check();
  }, [user, loading, navigate, getOnboarded]);

  useEffect(() => {
    getBookmarks().then(setBookmarked).catch(console.error);
    getWatchHistory().then(setWatchHistory).catch(console.error);
    getReviewPending().then(setReviewPending).catch(console.error);
    getHiddenTitles().then(setHiddenTitles).catch(console.error);
  }, [getBookmarks, getWatchHistory, getReviewPending, getHiddenTitles]);

  if (ageBlocked) return <AgeBlockedScreen />;

  return (
    <div className="min-h-[100dvh] bg-background pb-16">
      {needsReverification && (
        <AgeGateModal isReverification onComplete={() => setNeedsReverification(false)} onBlocked={() => setAgeBlocked(true)} />
      )}
      <div className="px-4 pt-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={ayerisLogo} alt="Ayeris" className="w-9 h-9 rounded-lg" />
            <h1 className="font-display text-2xl font-bold text-foreground">
              <span className="text-gradient-iris">Ayeris</span>
            </h1>
          </div>
          <button onClick={() => navigate("/friends")} className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
            <Users className="w-4 h-4 text-muted-foreground" />
          </button>
        </motion.div>
      </div>

      <div className="px-4 mb-6">
        <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} onClick={() => navigate("/discover")} className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-iris shadow-iris active:scale-[0.98] transition-transform">
          <div className="w-12 h-12 rounded-xl bg-primary-foreground/20 flex items-center justify-center">
            <Play className="w-6 h-6 text-primary-foreground" />
          </div>
          <div className="text-left">
            <p className="font-display text-lg font-bold text-primary-foreground">{t("home.startDiscovering")}</p>
            <p className="text-sm text-primary-foreground/70">{t("home.swipePicks")}</p>
          </div>
        </motion.button>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <ShelfRow title={t("home.toReview")} items={reviewPending.slice(0, 4)} emptyMessage={t("home.noReview")} onTitleClick={() => navigate("/review")} />
        <ShelfRow title={t("home.bookmarked")} items={bookmarked} emptyMessage={t("home.noBookmarks")} onTitleClick={() => navigate("/bookmarks")} />
        <ShelfRow title={t("home.watchHistory")} items={watchHistory.slice(0, 4)} emptyMessage={t("home.noHistory")} onTitleClick={() => navigate("/watch-history")} />
        {hiddenTitles.length > 0 && (
          <ShelfRow title={t("home.hiddenTitles")} items={hiddenTitles.slice(0, 4)} emptyMessage="" onTitleClick={() => navigate("/hidden-titles")} />
        )}
      </motion.div>
      <TmdbAttribution className="pt-6 pb-2" />
      <p className="text-[10px] text-muted-foreground/60 text-center mb-4">© 2026 Ayeris.tv — All rights reserved.</p>
    </div>
  );
};

export default Home;
